<template>
  <div ref="eaChartsRef01" style="height: 300px" />
</template>

<script>
import * as echarts from 'echarts';
import { debounce } from 'lodash-es';

/**
 * ECharts基础组件 - 班次作业效率折线图（白天模式）
 * 提供图表初始化、自适应缩放、配置更新等功能
 */
export default {
  name: 'EaCharts01',
  props: {
    paramsData: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      sortOnTimeRateArr: [],
      sendOnTimeRateArr: [],
      productivityArr: [],
      eaChartsRef01: null, // ECharts实例对象
      resizeHandler: null // 防抖的resize处理函数
    };
  },
  computed: {
    /**
     * 图表核心配置项（白天模式）
     */
    options() {
      return {
        title: {
          text: '班次作业效率 Eficiência operacional do turno',
          left: 'center',
          textStyle: {
            color: '#333', // 标题深色字体
            fontSize: 14
          }
        },
        tooltip: {
          trigger: 'axis',
          backgroundColor: 'rgba(255,255,255,0.9)', // 白色tooltip背景
          borderColor: '#ddd', // 边框浅灰色
          borderWidth: 1,
          textStyle: {
            color: '#333', // tooltip深色文字
            fontSize: 12
          },
          formatter: function(params) {
            const shiftName = params[0]?.name || '未知班次';
            let html = `<div style="padding: 4px; font-weight: 600;">${shiftName}<br/></div>`;
            params.forEach((item) => {
              const val = item.value === '-' ? '-' : `${item.value}%`;
              html += `<p style="margin: 2px 0; display: flex; align-items: center; font-size: 12px;">
                <span style="display: inline-block; width: 8px; height: 8px; border-radius: 50%; background: ${item.color}; margin-right: 4px;"></span>
                ${item.seriesName} <span style="margin-left: 8px;">${val}</span>
              </p>`;
            });
            return html;
          }
        },
        legend: {
          top: 25,
          data: [
            { name: '分拣及时率 eficiência de produção', icon: 'circle' },
            { name: '发货及时率 Taxa de entrega no prazo', icon: 'circle' },
            { name: '生产人数 Produtividade por pessoa', icon: 'circle' }
          ],
          textStyle: {
            color: '#333', // 图例深色文字
            fontSize: 10
          },
          itemGap: 8,
          itemWidth: 8,
          itemHeight: 8
        },
        grid: {
          left: '8%',
          right: '8%',
          bottom: '10%',
          top: '50%'
        },
        xAxis: {
          type: 'category',
          data: ['早班', '中班', '晚班'],
          axisLine: { lineStyle: { color: '#666', width: 1 }}, // 坐标轴深灰色
          axisLabel: { color: '#333', fontSize: 10 }, // 轴标签深色
          axisTick: { show: false }
        },
        yAxis: {
          type: 'value',
          min: 0,
          max: 100,
          axisLabel: {
            color: '#333', // 轴标签深色
            fontSize: 10,
            formatter: '{value}%'
          },
          splitLine: {
            lineStyle: { color: '#eee', type: 'dashed', width: 1 } // 网格线浅灰色
          },
          axisLine: { lineStyle: { color: '#666', width: 1 }} // 坐标轴深灰色
        },
        series: [
          {
            name: '分拣及时率 eficiência de produção',
            type: 'line',
            symbol: 'circle',
            symbolSize: 6,
            itemStyle: { color: '#9370DB' }, // 紫色（浅色系）
            lineStyle: { color: '#9370DB', width: 1 },
            data: this.sortOnTimeRateArr || [0, 0, 0]
          },
          {
            name: '发货及时率 Taxa de entrega no prazo',
            type: 'line',
            symbol: 'circle',
            symbolSize: 6,
            itemStyle: { color: '#FFC107' }, // 黄色（浅色系）
            lineStyle: { color: '#FFC107', width: 1 },
            data: this.sendOnTimeRateArr || [0, 0, 0]
          },
          {
            name: '生产人数 Produtividade por pessoa',
            type: 'line',
            symbol: 'circle',
            symbolSize: 6,
            itemStyle: { color: '#20C997' }, // 青色（浅色系）
            lineStyle: { color: '#20C997', width: 1 },
            data: this.productivityArr || [0, 0, 0]
          }
        ]
      };
    }
  },
  // 核心：监听props变化，数据更新自动重新渲染
  watch: {
    paramsData: {
      deep: true, // 监听对象深层变化
      handler() {
        this.renderChart(); // 数据变化触发重新渲染
      }
    }
  },
  mounted() {
    // 初始化resize监听（防抖）
    this.resizeHandler = debounce(() => this.eaChartsRef01?.resize(), 100);
    window.addEventListener('resize', this.resizeHandler);
    // 首次渲染图表
    this.renderChart();
  },
  beforeDestroy() {
    // 组件销毁前清理资源
    window.removeEventListener('resize', this.resizeHandler);
    if (this.eaChartsRef01) {
      this.eaChartsRef01.dispose(); // 销毁图表实例，释放内存
      this.eaChartsRef01 = null;
    }
  },
  methods: {
    /**
     * 统一渲染图表逻辑（解决异步数据+空值问题）
     */
    renderChart() {
      // 1. 空值兜底：防止chartData不存在时报错
      const chartData = this.paramsData.chartData || [];

      // 2. 处理数据（判空+转百分比+保留3位小数）
      this.sortOnTimeRateArr = chartData.map((item) => {
        const val = item?.sortOnTimeRate;
        if (!val || isNaN(parseFloat(val))) {
          return '0.00'; // 空值/非数字默认返回 0.000
        }
        return (parseFloat(val) * 100).toFixed(2);
      });

      this.sendOnTimeRateArr = chartData.map((item) => {
        const val = item?.sendOnTimeRate;
        if (!val || isNaN(parseFloat(val))) {
          return '0.00'; // 空值/非数字默认返回 0.000
        }
        return (parseFloat(val) * 100).toFixed(2);
      });

      this.productivityArr = chartData.map((item) => {
        const val = item?.productivity;
        if (!val || isNaN(parseFloat(val))) {
          return '0.000'; // 空值/非数字默认返回 0.000
        }
        return (parseFloat(val) * 100).toFixed(3);
      });

      // 3. 初始化/更新图表
      if (!this.eaChartsRef01 && this.$refs.eaChartsRef01) {
        this.eaChartsRef01 = echarts.init(this.$refs.eaChartsRef01);
      }
      if (this.eaChartsRef01) {
        this.eaChartsRef01.setOption(this.options);
      }
    },
    /**
     * 对外暴露更新图表配置的方法
     * @param {Object} newOptions 新的图表配置项（可选，不传则刷新当前配置）
     */
    updateChart(newOptions = {}) {
      if (this.eaChartsRef01) {
        this.eaChartsRef01.setOption({ ...this.options, ...newOptions }, true);
      }
    }
  }
};
</script>
